import type { Route } from "../../../utils/models/Route";
import { LineStringFeature } from "../../../utils/models/LineStringFeature";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../utils/models/MarkerFeature";
import { generateID } from "../../../utils/helpers/IDGenerator";
import { cumulativeDistances, pointAtDistance, MeasuredLine } from "../utils/trailLine";
import { haversineMeters, orderedDayStages, detectStageConnectors } from "./stage-connectors";
import { cutLineStringAt } from "../cutLineString";
import { mergeLineStrings } from "../mergeLineStrings";

/**
 * Day flags — the flag where one day ends and the next begins. Pure logic (no
 * map, no store, no network) so it can be unit tested.
 *
 * A flag always slides along the line the two days walk. Dropping it off the
 * line puts the night off the trail: the day walks out to it along a detour
 * and the next day walks back along the same detour, while the trail itself
 * stays exactly as it was.
 *
 * Nothing records that a junction is a detour. It's recognised from the routes
 * themselves (see junctionDetour), so it survives saving and reloading without
 * any extra state.
 */

/** Two points this close are the same place (allows for rounding on save). */
const SAME_POINT_M = 1;

/** A flag never sits closer than this to either end of the stretch it divides. */
export const MIN_DAY_M = 10;

type Coord = number[];

// Route geometry in the store is sometimes a class instance and sometimes a
// plain object (undo snapshots and route splits go through JSON), so only
// plain fields are read here — never methods.
const coordsOf = (lineString: any): Coord[] => lineString?.lineStringGeometry?.coordinates ?? [];
const markerCoord = (marker: any): Coord => marker?.markerGeometry?.coordinates ?? [];

function samePoint(a: Coord | undefined, b: Coord | undefined): boolean {
  return !!a && !!b && haversineMeters(a, b) <= SAME_POINT_M;
}

/** Whether `b` is `a` walked the other way: same points, opposite order. */
export function isMirroredLine(a: Coord[], b: Coord[]): boolean {
  if (a.length < 2 || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!samePoint(a[i], b[b.length - 1 - i])) return false;
  }
  return true;
}

/**
 * The detour at the junction between `prev` (a day's last route) and `next`
 * (the following day's first route), when there is one: prev's last leg runs
 * out to the night and next's first leg walks straight back along it.
 *
 * An out-and-back hike split at its turnaround looks the same at the
 * junction, but there the retracing carries on: the legs either side of the
 * turnaround mirror each other too. A real detour leaves the trail and comes
 * back to it, and the trail then carries on somewhere new. Treating a
 * turnaround as a detour would drop the walk out to it when the flag moves,
 * so that case is ruled out.
 */
export function junctionDetour(prev: Route, next: Route): { line: any } | null {
  const prevLines = prev.lineStrings ?? [];
  const nextLines = next.lineStrings ?? [];
  if (prevLines.length < 2 || nextLines.length < 2) return null;

  const out = prevLines[prevLines.length - 1];
  const back = nextLines[0];
  if (!isMirroredLine(coordsOf(out), coordsOf(back))) return null;

  const before = coordsOf(prevLines[prevLines.length - 2]);
  const after = coordsOf(nextLines[1]);
  if (isMirroredLine(before, after)) return null;

  return { line: out };
}

/** One continuous coordinate list for consecutive legs, plus where each leg starts in it. */
function joinLegs(lineStrings: any[]): { coordinates: Coord[]; legStartIndex: number[] } {
  const coordinates: Coord[] = [];
  const legStartIndex: number[] = [];
  for (const lineString of lineStrings) {
    const coords = coordsOf(lineString);
    const last = coordinates[coordinates.length - 1];
    // A leg that starts exactly where the previous one ended shares that
    // point; one that doesn't (a gap) keeps both, so the jump still counts.
    const sharesJoin = !!last && !!coords[0] && last[0] === coords[0][0] && last[1] === coords[0][1];
    legStartIndex.push(sharesJoin ? coordinates.length - 1 : coordinates.length);
    coordinates.push(...(sharesJoin ? coords.slice(1) : coords));
  }
  return { coordinates, legStartIndex };
}

function markerAt(coord: Coord, isSnap: boolean): MarkerFeature {
  return new MarkerFeature(
    new MarkerGeometry([coord[0], coord[1], coord[2] ?? 0]),
    new MarkerProperties(generateID(), isSnap, Date.now().toString(), coord[2] ?? 0)
  );
}

/** A leg walked the other way, keeping its surface/waytype data when it has any. */
export function reversedLeg(lineString: any): LineStringFeature {
  if (typeof lineString?.reverse === 'function') return lineString.reverse();
  return LineStringFeature.coordsOnlyFeature([...coordsOf(lineString)].reverse());
}

/**
 * A stretch of walking line with its control points: markers[i] and
 * markers[i + 1] are joined by lineStrings[i], and markerDistM[i] is how far
 * along `trail` markers[i] sits.
 */
export interface FlagLine {
  markers: any[];
  lineStrings: any[];
  trail: MeasuredLine;
  markerDistM: number[];
}

function flagLineOf(markers: any[], lineStrings: any[]): FlagLine | null {
  if (markers.length < 2 || lineStrings.length !== markers.length - 1) return null;
  if (lineStrings.some(lineString => coordsOf(lineString).length < 2)) return null;

  const { coordinates, legStartIndex } = joinLegs(lineStrings);
  const cumDist = cumulativeDistances(coordinates);
  const total = cumDist[cumDist.length - 1] ?? 0;
  const markerDistM = [...legStartIndex.map(index => cumDist[index]), total];
  return { markers, lineStrings, trail: { coordinates, cumDist }, markerDistM };
}

/** A single route as a FlagLine, for placing a new day boundary inside it. */
export function routeLine(route: Route): FlagLine | null {
  return flagLineOf([...route.markers], [...route.lineStrings]);
}

/**
 * The line two days walk together with the flag between them taken out, so
 * the flag can be put back anywhere along it (placeDayFlag). Any detour at the
 * junction comes out with it: the trail is what's left. `flagDistM` is where
 * the flag sits along that trail now (at its gate, for a detour).
 *
 * The two legs either side of the old flag merge into one, so the flag's old
 * spot doesn't linger as a control point. Every other leg is kept as it is.
 * Null when either route has no usable line.
 */
export function dayFlagLine(prev: Route, next: Route): (FlagLine & { flagDistM: number; detour: any | null }) | null {
  const prevMarkers = [...(prev.markers ?? [])];
  const prevLines = [...(prev.lineStrings ?? [])];
  const nextMarkers = [...(next.markers ?? [])];
  const nextLines = [...(next.lineStrings ?? [])];
  if (!flagLineOf(prevMarkers, prevLines) || !flagLineOf(nextMarkers, nextLines)) return null;

  const detour = junctionDetour(prev, next);
  if (detour) {
    prevMarkers.pop();
    prevLines.pop();
    nextMarkers.shift();
    nextLines.shift();
  }

  const flagIndex = prevMarkers.length - 1;
  const both = flagLineOf([...prevMarkers, ...nextMarkers.slice(1)], [...prevLines, ...nextLines]);
  if (!both) return null;
  const flagDistM = both.markerDistM[flagIndex];

  return { ...mergeAtMarkers(both, [flagIndex]), flagDistM, detour: detour?.line ?? null };
}

/**
 * The nearest point to `coord` anywhere along a line (not just at its
 * vertices — a straight leg drawn with routing off has none in between), how
 * far along the line it is, and how far `coord` is from it.
 */
export function nearestAlongLine(
  trail: MeasuredLine,
  coord: Coord,
  range?: { fromM: number; toM: number }
): { point: Coord; distanceM: number; offLineM: number } {
  const { coordinates, cumDist } = trail;
  if (coordinates.length === 0) return { point: coord, distanceM: 0, offLineM: Infinity };
  // Flat projection around the target: plenty for picking a point on a path.
  const kx = Math.cos((coord[1] * Math.PI) / 180);
  // With a `range`, only that stretch of the line counts — so a line that
  // passes the same spot twice (out and back) is matched on the right pass.
  let best = range
    ? { point: coord, distanceM: range.fromM, offLineM: Infinity }
    : { point: coordinates[0], distanceM: 0, offLineM: haversineMeters(coord, coordinates[0]) };
  for (let i = 1; i < coordinates.length; i++) {
    const a = coordinates[i - 1];
    const b = coordinates[i];
    const segmentM = cumDist[i] - cumDist[i - 1];
    let tMin = 0;
    let tMax = 1;
    if (range) {
      if (cumDist[i] < range.fromM || cumDist[i - 1] > range.toM) continue;
      if (segmentM > 0) {
        tMin = Math.max(0, (range.fromM - cumDist[i - 1]) / segmentM);
        tMax = Math.min(1, (range.toM - cumDist[i - 1]) / segmentM);
      }
    }
    const dx = (b[0] - a[0]) * kx;
    const dy = b[1] - a[1];
    const lengthSq = dx * dx + dy * dy;
    const t = lengthSq === 0
      ? tMin
      : Math.min(tMax, Math.max(tMin, (((coord[0] - a[0]) * kx) * dx + (coord[1] - a[1]) * dy) / lengthSq));
    const point = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, (a[2] ?? 0) + ((b[2] ?? 0) - (a[2] ?? 0)) * t];
    const offLineM = haversineMeters(coord, point);
    if (offLineM < best.offLineM) {
      best = { point, distanceM: cumDist[i - 1] + (cumDist[i] - cumDist[i - 1]) * t, offLineM };
    }
  }
  return best;
}

/** Keeps a flag MIN_DAY_M clear of both ends of a stretch `totalM` long (its middle, when too short for that). */
export function clampFlagDistance(distM: number, totalM: number): number {
  if (totalM <= MIN_DAY_M * 2) return totalM / 2;
  return Math.min(Math.max(distM, MIN_DAY_M), totalM - MIN_DAY_M);
}

export interface DayParts {
  prev: { markers: any[]; lineStrings: any[] };
  next: { markers: any[]; lineStrings: any[] };
}

/**
 * Splits a FlagLine into the day before a flag and the day after it, with the
 * flag `distM` along the line (clamped clear of either end). A flag that lands
 * on an existing control point uses it; otherwise the leg it lands in is cut
 * in two there.
 *
 * With `detour` (a leg from the flag's spot out to where the night is), the
 * earlier day walks out along it and the later day walks back.
 */
export function placeDayFlag(line: FlagLine, distM: number, isSnap: boolean, detour: any | null = null): DayParts {
  const markers = [...line.markers];
  const lineStrings = [...line.lineStrings];
  const markerDistM = line.markerDistM;
  const total = markerDistM[markerDistM.length - 1];
  const at = clampFlagDistance(distM, total);

  let leg = 0;
  while (leg < lineStrings.length - 1 && markerDistM[leg + 1] < at) leg++;

  let flagIndex: number;
  if (leg > 0 && at - markerDistM[leg] < SAME_POINT_M) {
    flagIndex = leg;
  } else if (leg + 1 < markers.length - 1 && markerDistM[leg + 1] - at < SAME_POINT_M) {
    flagIndex = leg + 1;
  } else {
    const coords = coordsOf(lineStrings[leg]);
    const cumDist = cumulativeDistances(coords);
    const legLength = cumDist[cumDist.length - 1];
    const offset = Math.min(Math.max(at - markerDistM[leg], 0), legLength);
    markers.splice(leg + 1, 0, markerAt(pointAtDistance(coords, cumDist, offset), isSnap));
    // Both halves keep the leg's turns, profile and surfaces (see cutLineString.ts).
    lineStrings.splice(leg, 1, ...cutLineStringAt(lineStrings[leg], offset));
    flagIndex = leg + 1;
  }

  const prev = { markers: markers.slice(0, flagIndex + 1), lineStrings: lineStrings.slice(0, flagIndex) };
  const next = {
    markers: [markerAt(markerCoord(markers[flagIndex]), isSnap), ...markers.slice(flagIndex + 1)],
    lineStrings: lineStrings.slice(flagIndex),
  };

  if (detour) {
    const night = coordsOf(detour)[coordsOf(detour).length - 1];
    prev.markers.push(markerAt(night, isSnap));
    prev.lineStrings.push(detour);
    next.markers.unshift(markerAt(night, isSnap));
    next.lineStrings.unshift(reversedLeg(detour));
  }

  return { prev, next };
}

/** Merges the two legs either side of each marker at `indexes` into one, dropping those markers. */
function mergeAtMarkers(line: FlagLine, indexes: number[]): FlagLine {
  const markers = [...line.markers];
  const lineStrings = [...line.lineStrings];
  const markerDistM = [...line.markerDistM];
  [...indexes].sort((a, b) => b - a).forEach(index => {
    // Turns, profile and surfaces carried across the join (see mergeLineStrings.ts).
    const merged = mergeLineStrings({ lineStrings: lineStrings as any }, index - 1, index);
    markers.splice(index, 1);
    markerDistM.splice(index, 1);
    lineStrings.splice(index - 1, 2, merged);
  });
  return { markers, lineStrings, trail: line.trail, markerDistM };
}

/** A day boundary along a whole multi-day line: how far along it, and the detour to the night if it's off the trail. */
export interface DayJunction {
  distM: number;
  detour: any | null;
}

/**
 * The whole walking line of a multi-day plan — `dayRoutes` is each day's
 * route in order, one per day, each joined to the next — with every flag and
 * detour taken out, plus where each flag was. Splitting it again at new
 * distances (splitIntoDayParts) re-spaces the days while keeping every
 * control point that isn't a flag. Null when a route has no usable line.
 */
export function wholeDayLine(dayRoutes: Route[]): (FlagLine & { junctions: DayJunction[] }) | null {
  if (dayRoutes.length === 0) return null;
  const detours = dayRoutes.slice(1).map((next, i) => junctionDetour(dayRoutes[i], next)?.line ?? null);

  const markers: any[] = [];
  const lineStrings: any[] = [];
  const flagIndexes: number[] = [];
  for (let i = 0; i < dayRoutes.length; i++) {
    const dayMarkers = [...(dayRoutes[i].markers ?? [])];
    const dayLines = [...(dayRoutes[i].lineStrings ?? [])];
    if (!flagLineOf(dayMarkers, dayLines)) return null;
    if (i > 0 && detours[i - 1]) {
      dayMarkers.shift();
      dayLines.shift();
    }
    if (i < dayRoutes.length - 1 && detours[i]) {
      dayMarkers.pop();
      dayLines.pop();
    }
    if (dayLines.length === 0) return null;

    if (i === 0) {
      markers.push(...dayMarkers);
    } else {
      flagIndexes.push(markers.length - 1);
      markers.push(...dayMarkers.slice(1));
    }
    lineStrings.push(...dayLines);
  }

  const line = flagLineOf(markers, lineStrings);
  if (!line) return null;
  const junctions = flagIndexes.map((index, i) => ({ distM: line.markerDistM[index], detour: detours[i] }));
  return { ...mergeAtMarkers(line, flagIndexes), junctions };
}

/**
 * Cuts a line into one part per day at each of `flags` (in any order), each
 * with its detour if it has one. The parts come back in walking order.
 */
export function splitIntoDayParts(
  line: FlagLine,
  flags: DayJunction[],
  isSnap: boolean
): Array<{ markers: any[]; lineStrings: any[] }> | null {
  const sorted = [...flags].sort((a, b) => a.distM - b.distM);
  const parts: Array<{ markers: any[]; lineStrings: any[] }> = [];
  let rest: FlagLine = line;
  // Last flag first: everything before a flag keeps the same distances, so
  // the earlier flags still land where they should in what's left.
  for (let i = sorted.length - 1; i >= 0; i--) {
    const { prev, next } = placeDayFlag(rest, sorted[i].distM, isSnap, sorted[i].detour);
    parts.unshift(next);
    const prevLine = flagLineOf(prev.markers, prev.lineStrings);
    if (!prevLine) return null;
    rest = prevLine;
  }
  parts.unshift({ markers: rest.markers, lineStrings: rest.lineStrings });
  return parts;
}

/**
 * Where the day boundaries go when `dayCount` days are spaced as evenly as
 * they can be along `totalM` metres, keeping every boundary in `fixedM` where
 * it is (overnight stops the user has pinned to a place). The free boundaries
 * go to whichever stretch between pinned ones has the longest days, one at a
 * time, then each stretch is divided evenly.
 *
 * Returns dayCount - 1 distances in order, the pinned ones included as given.
 * With more pinned stops than that allows, every pinned stop is still kept.
 */
export function evenDayBoundaries(totalM: number, fixedM: number[], dayCount: number): number[] {
  const fixed = [...new Set(fixedM)].filter(d => d > 0 && d < totalM).sort((a, b) => a - b);
  const edges = [0, ...fixed, totalM];
  const stretches = edges.slice(1).map((end, i) => ({ start: edges[i], length: end - edges[i], days: 1 }));

  let free = Math.max(0, dayCount - 1 - fixed.length);
  while (free > 0) {
    let longest = stretches[0];
    for (const stretch of stretches) {
      if (stretch.length / stretch.days > longest.length / longest.days) longest = stretch;
    }
    longest.days++;
    free--;
  }

  const boundaries: number[] = [];
  stretches.forEach((stretch, i) => {
    for (let day = 1; day < stretch.days; day++) {
      boundaries.push(stretch.start + (stretch.length * day) / stretch.days);
    }
    if (i < fixed.length) boundaries.push(fixed[i]);
  });
  return boundaries;
}

/**
 * Why the days can't be spaced evenly right now, or null when they can.
 * Spacing needs one continuous line: each day a single route, each joined to
 * the next.
 */
export function daySpacingProblem(routes: { [key: string]: Route }, disconnectedStageIds: string[] = []):
  | { kind: 'no-route' }
  | { kind: 'several-routes'; day: number }
  | { kind: 'not-joined'; day: number }
  | null {
  const stages = orderedDayStages(routes);
  if (stages.length === 0) return { kind: 'no-route' };
  const multi = stages.find(stage => stage.firstKey !== stage.lastKey);
  if (multi) return { kind: 'several-routes', day: multi.dayNumber };

  const joined = new Set(detectStageConnectors(routes, undefined, disconnectedStageIds).map(c => c.prevRouteKey));
  const gap = stages.slice(0, -1).find(stage => !joined.has(stage.lastKey));
  if (gap) return { kind: 'not-joined', day: gap.dayNumber };
  return null;
}
